import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';
import { InputText } from 'primereact/inputtext';
import SettlementService from '../../services/SettlementService';
import { useNavigate } from 'react-router-dom';

const SettlementListPage = () => {
    const [mounted, setMounted] = useState(false);
    const [settlements, setSettlements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [globalFilter, setGlobalFilter] = useState('');
    const toast = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        setMounted(true);
        loadSettlements();
    }, []);

    const loadSettlements = async () => {
        setLoading(true);
        try {
            const data = await SettlementService.getSettlements();
            setSettlements(data);
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Mahsuplaşma listesi yüklenemedi.', life: 3000 });
        } finally {
            setLoading(false);
        }
    };

    const confirmDelete = (rowData) => {
        confirmDialog({
            message: 'Mahsuplaşma işlemi silinsin mi? Bu işlem geri alınamaz ve her iki cari hesaptaki hareketler de silinecektir.',
            header: 'İşlemi Onayla',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Evet, Sil',
            rejectLabel: 'Vazgeç',
            acceptClassName: 'p-button-danger rounded-xl',
            rejectClassName: 'p-button-text p-button-secondary rounded-xl',
            accept: () => handleDelete(rowData.id)
        });
    };

    const handleDelete = async (id) => {
        try {
            const res = await SettlementService.deleteSettlement(id);
            if (res.success) {
                toast.current.show({ severity: 'success', summary: 'Başarılı', detail: 'Mahsuplaşma işlemi silindi.', life: 3000 });
                loadSettlements();
            } else {
                toast.current.show({ severity: 'error', summary: 'Hata', detail: res.message, life: 3000 });
            }
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Silme işlemi sırasında hata oluştu.', life: 3000 });
        }
    };

    const dateTemplate = (rowData) => {
        return (
            <div className="flex flex-col">
                <span className="font-bold text-slate-700">{new Date(rowData.date).toLocaleDateString('tr-TR')}</span>
                <span className="text-[10px] text-slate-400 uppercase font-black tracking-tighter">İşlem Tarihi</span>
            </div>
        );
    };

    const amountTemplate = (rowData) => {
        return (
            <div className="bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2 inline-block">
                <span className="font-black text-indigo-700">{rowData.amount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</span>
            </div>
        );
    };

    const statusTemplate = (rowData) => {
        if (rowData.status === 1) {
            return (
                <div className="flex items-center gap-2 text-emerald-600 font-bold text-sm">
                    <i className="pi pi-check-circle" />
                    <span>Aktif</span>
                </div>
            );
        }
        return (
            <div className="flex items-center gap-2 text-red-500 font-bold text-sm">
                <i className="pi pi-times-circle" />
                <span>İptal Edildi</span>
            </div>
        );
    };

    const entityTemplate = (name, type) => {
        const isCustomer = type === 'customer';
        return (
            <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isCustomer ? 'bg-blue-100 text-blue-600' : 'bg-emerald-100 text-emerald-600'}`}>
                    <i className={`pi ${isCustomer ? 'pi-user' : 'pi-truck'} text-xs`} />
                </div>
                <span className="font-bold text-slate-700">{name}</span>
            </div>
        );
    };

    const actionTemplate = (rowData) => {
        return (
            <div className="flex gap-1 justify-end">
                <Button
                    icon="pi pi-pencil"
                    className="p-button-rounded p-button-text p-button-sm p-button-warning"
                    tooltip="Düzenle"
                    onClick={() => navigate(`/finance/settlements/edit/${rowData.id}`)}
                />
                <Button
                    icon="pi pi-trash"
                    className="p-button-rounded p-button-text p-button-sm p-button-danger"
                    tooltip="Sil"
                    onClick={() => confirmDelete(rowData)}
                />
                <Button
                    icon="pi pi-arrow-right"
                    className="p-button-rounded p-button-text p-button-sm"
                    tooltip="Müşteri Detayı"
                    onClick={() => navigate(`/customers/detail/${rowData.customerId}`)}
                />
            </div>
        );
    };

    const header = (
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 py-2">
            <div className="relative w-full md:w-96">
                <i className="pi pi-search absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <InputText
                    type="search"
                    onInput={(e) => setGlobalFilter(e.target.value)}
                    placeholder="Müşteri, tedarikçi veya açıklama ara..."
                    className="w-full pl-11 !h-12 !rounded-2xl border-slate-200 bg-slate-50 focus:bg-white transition-all shadow-none"
                />
            </div>
            <Button
                label="YENİ MAHSUPLAŞMA"
                icon="pi pi-plus-circle"
                className="!bg-indigo-600 !border-indigo-600 !rounded-2xl !h-12 px-6 font-black text-xs tracking-widest shadow-lg shadow-indigo-100"
                onClick={() => navigate('/finance/settlements')}
            />
        </div>
    );

    return (
        <div className="max-w-7xl mx-auto p-2 animate-fade-in">
            <Toast ref={toast} appendTo={mounted ? document.body : "self"} />
            <ConfirmDialog appendTo={mounted ? document.body : "self"} />

            <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 mb-6 flex flex-col md:flex-row justify-between items-center gap-4">
                <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-indigo-50 flex items-center justify-center text-indigo-600 border border-indigo-100">
                        <i className="pi pi-history text-2xl" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-black text-slate-800 leading-tight">Mahsuplaşma Geçmişi</h1>
                        <p className="text-sm text-slate-500">Cari hesaplar arasında yapılmış tüm virman ve mahsup kayıtları.</p>
                    </div>
                </div>
                <div className="flex bg-slate-50 p-2 rounded-2xl border border-slate-100 gap-4 px-6">
                    <div className="flex flex-col">
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Toplam İşlem</span>
                        <span className="text-lg font-black text-indigo-600">{settlements.length} Adet</span>
                    </div>
                    <Divider layout="vertical" />
                    <div className="flex flex-col">
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Toplam Tutar</span>
                        <span className="text-lg font-black text-slate-800">
                            {settlements.filter(x => x.status === 1).reduce((a, b) => a + b.amount, 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                        </span>
                    </div>
                </div>
            </div>

            <Card className="shadow-sm border-slate-200 rounded-[2rem] overflow-hidden border-none shadow-xl shadow-slate-200/50">
                <DataTable
                    value={settlements}
                    loading={loading}
                    paginator
                    rows={10}
                    header={header}
                    globalFilter={globalFilter}
                    responsiveLayout="stack"
                    breakpoint="960px"
                    emptyMessage="Henüz bir mahsuplaşma işlemi bulunamadı."
                    className="p-datatable-modern"
                    rowHover
                    dataKey="id"
                >
                    <Column field="date" header="TARİH" body={dateTemplate} sortable style={{ width: '150px' }} className="py-4" />
                    <Column field="customerName" header="MÜŞTERİ" body={(r) => entityTemplate(r.customerName, 'customer')} sortable />
                    <Column field="supplierName" header="TEDARİKÇİ" body={(r) => entityTemplate(r.supplierName, 'supplier')} sortable />
                    <Column field="amount" header="TUTAR" body={amountTemplate} sortable style={{ width: '180px' }} />
                    <Column field="description" header="AÇIKLAMA" className="text-slate-500 text-sm italic" />
                    <Column field="status" header="DURUM" body={statusTemplate} sortable style={{ width: '140px' }} />
                    <Column body={actionTemplate} header="İŞLEM" style={{ width: '100px' }} />
                </DataTable>
            </Card>
        </div>
    );
};

const Divider = ({ layout = "horizontal" }) => (
    <div className={`${layout === "vertical" ? "w-px h-10 bg-slate-200 mx-2" : "h-px w-full bg-slate-200 my-2"}`} />
);

export default SettlementListPage;
